// Phase C2 — React Query hooks for /wb/barriers.
//
// Server module: server/src/modules/wb/barriers. Connection config is
// JSON-stringified by the client and never returned in any response
// (server encrypts at rest). trigger_config_json is an ARRAY of rules,
// max 20 — not a wrapper object.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Types ------------------------------------------------------------------

export type WbBarrierType =
  | 'traffic_light'
  | 'barrier_arm'
  | 'siren'
  | 'generic_signal';

export type WbBarrierProtocol =
  | 'usb_relay_hid'
  | 'modbus_tcp'
  | 'mqtt'
  | 'gpio';

export type WbTriggerEvent =
  | 'ticket.completed'
  | 'ticket.gross_captured'
  | 'weight.stable_no_action'
  | 'overweight.detected'
  | 'manual';

export interface WbTriggerRule {
  event: WbTriggerEvent;
  condition?: Record<string, unknown>;
  target_state: string;
  hold_ms?: number;
}

export interface WbBarrier {
  enc_id: string;
  yard: { enc_id: string; name: string };
  weighbridge: { enc_id: string; name: string } | null;
  name: string;
  code: string;
  type: WbBarrierType;
  protocol: WbBarrierProtocol;
  default_state: string;
  current_state: string;
  trigger_config_json: ReadonlyArray<WbTriggerRule> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateBarrierBody {
  yard_id: string;
  weighbridge_id?: string | null;
  name: string;
  code: string;
  type: WbBarrierType;
  protocol: WbBarrierProtocol;
  connection_config_encrypted?: string;
  default_state?: string;
  trigger_config_json?: WbTriggerRule[];
  is_active?: boolean;
}

export interface UpdateBarrierBody {
  weighbridge_id?: string | null;
  name?: string;
  code?: string;
  type?: WbBarrierType;
  protocol?: WbBarrierProtocol;
  connection_config_encrypted?: string | null;
  default_state?: string;
  trigger_config_json?: WbTriggerRule[];
  is_active?: boolean;
}

// -- List -------------------------------------------------------------------

export interface UseWbBarriersOpts {
  weighbridgeEncId?: string;
  yardEncId?: string;
}

export function useWbBarriers(opts: UseWbBarriersOpts = {}) {
  const filters: Record<string, string> = {};
  if (opts.weighbridgeEncId) filters['weighbridge_id'] = opts.weighbridgeEncId;
  if (opts.yardEncId) filters['yard_id'] = opts.yardEncId;
  const qs = new URLSearchParams(filters).toString();
  const url = qs ? `${endpoints.wb.barriers}?${qs}` : endpoints.wb.barriers;
  return useQuery({
    queryKey: queryKeys.wb.barriers(filters),
    queryFn: async () => {
      const res = await apiClient.get<{ barriers: WbBarrier[] }>(url);
      return res.data;
    },
  });
}

// -- Detail -----------------------------------------------------------------

export function useWbBarrier(encId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.wb.barrier(encId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<{ barrier: WbBarrier }>(
        endpoints.wb.barrier(encId as string),
      );
      return res.data;
    },
    enabled: Boolean(encId) && encId !== 'new',
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateBarrier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBarrierBody) => {
      const res = await apiClient.post<{ barrier: WbBarrier }, CreateBarrierBody>(
        endpoints.wb.barriers,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wb', 'barriers'] });
    },
  });
}

export function useUpdateBarrier(encId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateBarrierBody) => {
      const res = await apiClient.patch<{ barrier: WbBarrier }, UpdateBarrierBody>(
        endpoints.wb.barrier(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wb', 'barriers'] });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.barrier(encId) });
    },
  });
}

export function useDeleteBarrier(encId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.barrier(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wb', 'barriers'] });
    },
  });
}
